import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ConnectivityProvider } from '@/contexts/ConnectivityContext';
import { BolaoProvider } from '@/contexts/BolaoContext';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { Header } from '@/components/ui/Header';
import { BottomNav } from '@/components/ui/BottomNav';
import { OfflineBanner } from '@/components/ui/OfflineBanner';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAndroidBackButton } from '@/hooks/useAndroidBackButton';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';

/* ── Lazy-loaded pages ── */
const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'));
const SignupPage = React.lazy(() => import('@/pages/auth/SignupPage'));
const ConfirmPage = React.lazy(() => import('@/pages/auth/ConfirmPage'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage'));
const HomePage = React.lazy(() => import('@/pages/home/HomePage'));
const BoloesListPage = React.lazy(() => import('@/pages/boloes/BoloesListPage'));
const BolaoSearchPage = React.lazy(() => import('@/pages/boloes/BolaoSearchPage'));
const BolaoLandingPage = React.lazy(() => import('@/pages/boloes/BolaoLandingPage'));
const CreateBolaoPage = React.lazy(() => import('@/pages/boloes/CreateBolaoPage'));
const BolaoDetailPage = React.lazy(() => import('@/pages/boloes/BolaoDetailPage'));
const EditBolaoPage = React.lazy(() => import('@/pages/boloes/EditBolaoPage'));
const BolaoAdminPage = React.lazy(() => import('@/pages/boloes/BolaoAdminPage'));
const ParticipantesAdminPage = React.lazy(() => import('@/pages/boloes/ParticipantesAdminPage'));
const InvitePage = React.lazy(() => import('@/pages/boloes/InvitePage'));
const PalpitesJogosPage = React.lazy(() => import('@/pages/palpites/PalpitesJogosPage'));
const PalpitesEspeciaisPage = React.lazy(() => import('@/pages/palpites/PalpitesEspeciaisPage'));
const RankingPage = React.lazy(() => import('@/pages/ranking/RankingPage'));
const ScoreDetailPage = React.lazy(() => import('@/pages/ranking/ScoreDetailPage'));
const ComparisonPage = React.lazy(() => import('@/pages/ranking/ComparisonPage'));
const StatsPage = React.lazy(() => import('@/pages/ranking/StatsPage'));
const NotificationsPage = React.lazy(() => import('@/pages/notifications/NotificationsPage'));
const NotifPreferencesPage = React.lazy(() => import('@/pages/notifications/NotifPreferencesPage'));
const ProfilePage = React.lazy(() => import('@/pages/profile/ProfilePage'));
const EditProfilePage = React.lazy(() => import('@/pages/profile/EditProfilePage'));
const ChangePasswordPage = React.lazy(() => import('@/pages/profile/ChangePasswordPage'));
const AuditoriaPage = React.lazy(() => import('@/pages/auditoria/AuditoriaPage'));
const AuditoriaDetailPage = React.lazy(() => import('@/pages/auditoria/AuditoriaDetailPage'));
const AuditoriaEspeciaisPage = React.lazy(() => import('@/pages/auditoria/AuditoriaEspeciaisPage'));
const PrivacyPolicyPage = React.lazy(() => import('@/pages/PrivacyPolicyPage'));
const AccountDeletionPage = React.lazy(() => import('@/pages/AccountDeletionPage'));
const SupportPage = React.lazy(() => import('@/pages/SupportPage'));

/* ── Loading fallback ── */
function PageLoader() {
  return (
    <div style={{ padding: 'var(--spacing-16)' }}>
      <Skeleton width="60%" height="24px" />
      <div style={{ marginTop: 'var(--spacing-12)' }}>
        <Skeleton width="100%" height="120px" borderRadius="var(--radius-md)" />
      </div>
      <div style={{ marginTop: 'var(--spacing-12)' }}>
        <Skeleton width="100%" height="120px" borderRadius="var(--radius-md)" />
      </div>
    </div>
  );
}

/* ── ProtectedRoute ── */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  return <>{children}</>;
}

/* ── Layout with Header + BottomNav ── */
function Layout({ children }: { children: React.ReactNode }) {
  const { unreadCount } = useUnreadNotifications();

  return (
    <>
      <Header unreadCount={unreadCount} />
      <OfflineBanner />
      <main
        style={{
          flex: 1,
          paddingTop: '56px',
          paddingBottom: '64px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </main>
      <BottomNav />
    </>
  );
}

/* ── App ── */
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <ConnectivityProvider>
            <BolaoProvider>
              <AppRoutes />
          </BolaoProvider>
        </ConnectivityProvider>
      </ToastProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  useAndroidBackButton();

  return (
              <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/confirm" element={<ConfirmPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/privacidade" element={<PrivacyPolicyPage />} />
                <Route path="/excluir-conta" element={<AccountDeletionPage />} />
                <Route path="/suporte" element={<SupportPage />} />

                {/* Protected routes with Layout */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout><HomePage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes"
                  element={
                    <ProtectedRoute>
                      <Layout><BoloesListPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/buscar"
                  element={
                    <ProtectedRoute>
                      <Layout><BolaoSearchPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/buscar/:codigo"
                  element={
                    <ProtectedRoute>
                      <Layout><BolaoSearchPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/criar"
                  element={
                    <ProtectedRoute>
                      <Layout><CreateBolaoPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bolao/:codigo"
                  element={
                    <Layout><BolaoLandingPage /></Layout>
                  }
                />
                <Route
                  path="/boloes/:bolaoId"
                  element={
                    <ProtectedRoute>
                      <Layout><BolaoDetailPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/editar"
                  element={
                    <ProtectedRoute>
                      <Layout><EditBolaoPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/admin"
                  element={
                    <ProtectedRoute>
                      <Layout><BolaoAdminPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/participantes"
                  element={
                    <ProtectedRoute>
                      <Layout><ParticipantesAdminPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/convite"
                  element={
                    <ProtectedRoute>
                      <Layout><InvitePage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/palpites"
                  element={
                    <ProtectedRoute>
                      <Layout><PalpitesJogosPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/palpites/especiais"
                  element={
                    <ProtectedRoute>
                      <Layout><PalpitesEspeciaisPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/ranking"
                  element={
                    <ProtectedRoute>
                      <Layout><RankingPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/ranking/:userId"
                  element={
                    <ProtectedRoute>
                      <Layout><ScoreDetailPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/comparacao"
                  element={
                    <ProtectedRoute>
                      <Layout><ComparisonPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/estatisticas"
                  element={
                    <ProtectedRoute>
                      <Layout><StatsPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/auditoria"
                  element={
                    <ProtectedRoute>
                      <Layout><AuditoriaPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/auditoria/especiais"
                  element={
                    <ProtectedRoute>
                      <Layout><AuditoriaEspeciaisPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boloes/:bolaoId/auditoria/:gameId"
                  element={
                    <ProtectedRoute>
                      <Layout><AuditoriaDetailPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notificacoes"
                  element={
                    <ProtectedRoute>
                      <Layout><NotificationsPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notificacoes/preferencias"
                  element={
                    <ProtectedRoute>
                      <Layout><NotifPreferencesPage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/perfil"
                  element={
                    <ProtectedRoute>
                      <Layout><ProfilePage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/perfil/editar"
                  element={
                    <ProtectedRoute>
                      <Layout><EditProfilePage /></Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/perfil/senha"
                  element={
                    <ProtectedRoute>
                      <Layout><ChangePasswordPage /></Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
  );
}
